<script lang="ts">
    import PanelistSurveyCriterionPage from '@Evaluation/AlternativesSurveys/Pages/PanelistSurveyCriterionPage'
    export default PanelistSurveyCriterionPage
</script>

<template>
    <div>
        <CriterionDetailsDialog :viewModel="criterionDetailsDialog" />

        <AlternativeDetailsDialog :viewModel="alternativeDetailsDialog" />

        <div class="container">
            <h3 class="text-center" v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

            <template v-if="initialized === true">
                <nav class="navbar fixed-bottom navbar-dark bg-dark">
                    <div class="container d-flex justify-content-center">
                        <div class="btn-group" role="group">
                            <a class="btn btn-secondary" v-sanitizedHref="`/#/scoring/criterion/${workspaceId}/${surveyId}/${projectUserId}/${prevCriterionId}`">
                                <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                                Prev <span class="text-lowercase d-none d-sm-inline">criterion</span>
                            </a>
                            <button type="button" class="btn btn-warning" @click="scrollTop">
                                Back to Top
                            </button>
                            <a class="btn btn-secondary" v-sanitizedHref="`/#/scoring/criterion/${workspaceId}/${surveyId}/${projectUserId}/${nextCriterionId}`">
                                Next <span class="text-lowercase d-none d-sm-inline">criterion</span>
                                <i class="fa fa-angle-double-right" aria-hidden="true"></i>
                            </a>
                        </div>
                    </div>
                </nav>

                <nav class="navbar navbar-expand-md navbar-dark bg-dark fixed-top">
                    <div class="container">
                        <a class="btn btn-light mr-auto" v-sanitizedHref="`/#/scoring/dashboard/${workspaceId}/${surveyId}/${projectUserId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                            My Survey
                        </a>

                        <div class="progress mx-2 w-50" style="height: 38px; background-color: #D3D3D3;">
                            <div class="progress-bar" role="progressbar" :style="{'width': progress + '%'}">{{AnsweredQuestionsCount}}/{{questionsCount}}</div>
                        </div>

                        <button v-if="IsSaved == null" type="button" class="btn btn-warning" @click="handleSavePanelistVotesCommand">
                            <i class="fa fa-spinner fa-spin"></i> Saving...
                        </button>
                        <button v-if="IsSaved === true" type="button" class="btn btn-light" @click="handleSavePanelistVotesCommand">
                            Saved
                        </button>
                        <button v-if="IsSaved === false" type="button" class="btn btn-warning" @click="handleSavePanelistVotesCommand">
                            Save
                        </button>
                    </div>
                </nav>

                <div style="padding-top: 80px; padding-bottom: 120px;">
                    <h3 id="top" class="mt-4">
                        <span class="font-weight-light">Evaluate <span class="text-lowercase">{{alternativesLabel}}</span> in the context of:</span> {{criterionName}}
                    </h3>
                    <hr />

                    <nav class="navbar navbar-expand-md navbar-light bg-light">
                        <ul class="navbar-nav">
                            <li class="nav-item dropdown">
                                <a class="nav-link dropdown-toggle" id="navbarDropdown" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                    Questions: {{QuestionsFilter ?? 'All'}}
                                </a>
                                <div class="dropdown-menu">
                                    <a class="dropdown-item" @click="QuestionsFilter = null">All <span class="badge badge-secondary">{{questionsCount}}</span></a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Answered'">Answered <span class="badge badge-secondary">{{AnsweredQuestionsCount}}</span></a>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Not answered'">Not answered <span class="badge badge-secondary">{{NotAnsweredQuestionsCount}}</span></a>
                                    <div class="dropdown-divider"></div>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Commented'">Commented <span class="badge badge-secondary">{{CommentedQuestionsCount}}</span></a>
                                    <a class="dropdown-item" @click="QuestionsFilter = 'Not commented'">Not commented <span class="badge badge-secondary">{{NotCommentedQuestionsCount}}</span></a>
                                </div>
                            </li>
                            <li v-if="isPairwise" class="nav-item dropdown">
                                <a class="nav-link dropdown-toggle" role="button" data-toggle="dropdown" @click="loadConsistencyAnalysis">
                                    Most inconsistent
                                </a>
                                <div class="dropdown-menu">
                                    <template v-if="consistencyAnalysis == null">
                                        <p class="m-4"><i class="fa fa-spinner fa-spin"></i></p>
                                    </template>
                                    <template v-else>
                                        <template v-if="consistencyAnalysis.InsufficientData === true">
                                            <p class="px-2">Not enough answers to check consistency</p>
                                        </template>
                                        <template v-if="consistencyAnalysis.IsConsistent === true">
                                            <a class="dropdown-item" @click="showConsistencyAnalysisLegend">
                                                <span class="fa-stack fa-lg fa-stack-sm">
                                                    <i class="fa fa-circle fa-stack-1x text-success"></i>
                                                    <i class="fa-regular fa-face-smile fa-stack-1x"></i>
                                                </span>
                                                Inconsistency score: {{consistencyAnalysis.CR}}%
                                            </a>
                                        </template>
                                        <template v-if="consistencyAnalysis.IsAcceptable === true">
                                            <a v-for="q in MostInconsistent" :key="q.Id" class="dropdown-item" @click="highlightQuestion(q)">
                                                {{q.Name1}} vs. {{q.Name2}}
                                            </a>
                                            <div class="dropdown-divider"></div>
                                            <a class="dropdown-item" @click="showConsistencyAnalysisLegend">
                                                <span class="fa-stack fa-lg fa-stack-sm">
                                                    <i class="fa fa-circle fa-stack-1x text-warning"></i>
                                                    <i class="fa-regular fa-face-meh fa-stack-1x"></i>
                                                </span>
                                                Inconsistency score: {{consistencyAnalysis.CR}}%
                                            </a>
                                        </template>
                                        <template v-if="consistencyAnalysis.IsInconsistent === true">
                                            <a v-for="q in MostInconsistent" :key="q.Id" class="dropdown-item" @click="highlightQuestion(q)">
                                                {{q.Name1}} vs. {{q.Name2}}
                                            </a>
                                            <div class="dropdown-divider"></div>
                                            <a class="dropdown-item" @click="showConsistencyAnalysisLegend">
                                                <span class="fa-stack fa-lg fa-stack-sm">
                                                    <i class="fa fa-circle fa-stack-1x text-danger"></i>
                                                    <i class="fa-regular fa-face-frown fa-stack-1x"></i>
                                                </span>
                                                Inconsistency score: {{consistencyAnalysis.CR}}%
                                            </a>
                                        </template>
                                    </template>
                                </div>
                            </li>
                        </ul>
                        <form class="form-inline my-2 my-lg-0 ml-auto">
                            <input class="form-control mr-sm-2" type="search" placeholder="Filter..." v-model="SearchPhrase">
                        </form>
                    </nav>

                    <div v-for="dr in DirectRatesView" :key="dr.Id">
                        <p class="mt-5 mb-3 lead">
                            <strong>{{dr.OrderValue}}.</strong>
                            How would you rate <span class="text-lowercase">{{alternativeLabel}}</span>
                            <a class="text-primary" @click="ShowAlternativeDetailsCommand(dr.AlternativeName, dr.AlternativeDescription)">{{dr.AlternativeName}}</a>
                            in the context of <a class="text-dark font-italic" @click="ShowDetailsCommand">{{criterionName}}</a>?
                        </p>

                        <div class="row">
                            <div class="col-md-8 col-sm-12">
                                <PanelistSurveyVoteEditorDr :propValue="dr.Value"
                                                            :propComment="dr.Comment"
                                                            :propScaleLevels="scaleLevels"
                                                            @update-value="updateValue(dr, $event)"
                                                            @update-comment="updateComment(dr, $event)" />
                            </div>
                        </div>
                    </div>

                    <div v-for="pc in PairwiseComparisonsView" :key="pc.Id">
                        <div class="row justify-content-start" :id="pc.DomId">
                            <div class="col-md-8 col-sm-12" :class="{'bg-warning': pc.Highlighted}">
                                <p class="mt-5 mb-3 lead">
                                    <strong>{{pc.OrderValue}}.</strong>
                                    In the context of <a class="text-dark font-italic" @click="ShowDetailsCommand">{{criterionName}}</a>,
                                    which <span class="text-lowercase">{{alternativeLabel}}</span> do you prefer: <br />
                                    <a class="text-primary" @click="ShowAlternativeDetailsCommand(pc.FirstAlternativeName, pc.FirstAlternativeDescription)">{{pc.FirstAlternativeName}}</a> or
                                    <a class="text-primary" @click="ShowAlternativeDetailsCommand(pc.SecondAlternativeName, pc.SecondAlternativeDescription)">{{pc.SecondAlternativeName}}</a>?
                                </p>

                                <PanelistSurveyVoteEditorPc :propValue="pc.Value"
                                                            :propComment="pc.Comment"
                                                            :propFirstAlternativeName="pc.FirstAlternativeName"
                                                            :propSecondAlternativeName="pc.SecondAlternativeName"
                                                            @update-value="updateValue(pc, $event)"
                                                            @update-comment="updateComment(pc, $event)" />
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>
